import styled from 'styled-components';
import {
  CellWithDropdown,
  DatasourceTable,
  newTableTheme,
  useSortableTable,
} from '@ardoq/table-legacy';
import { ExpandRowButton } from 'integrations/common/components/expandRowButton/ExpandRowButton';
import { useState } from 'react';
import { useEditableConfig } from 'integrations/common/components/editableCell/useEditableConfig';
import { smartSort } from '@ardoq/pagination';
import { ExpandedSchedule } from 'integrations/common/components/expandedSchedule/ExpandedSchedule';
import { EditableCell } from 'integrations/common/components/editableCell/EditableCell';
import { SchedulePolicy } from 'integrations/common/components/schedulePolicy/SchedulePolicy';
import { IntegrationDate } from 'integrations/common/components/integrationDate/IntegrationDate';
import { ScheduleStatus } from 'integrations/common/components/scheduleStatus/ScheduleStatus';
import { getScheduleDirectionId } from 'integrations/common/streams/schedules/utils';
import { DataSourceEmptyTable } from '@ardoq/integrations';
import { viewModel$ } from './viewModel$';
import { connect } from '@ardoq/rxbeach';
import { SortOrder, IntegrationSchedule } from '@ardoq/api-types';
import { IntegrationWorkspace } from '@ardoq/api-types/integrations';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type SchedulesComponentProps = {
  isLoading: boolean;
  schedules: IntegrationSchedule[];
  getConnectionName: (schedule: IntegrationSchedule) => string | null;
  onRenameSchedule: (name: string, schedule: IntegrationSchedule) => void;
  onStartScheduleRenaming: () => void;
  onPauseSchedule: (scheduleId: string) => void;
  onRunSchedule: (scheduleId: string) => void;
  onEditSchedule: (id: string) => void;
  onStartCreateConfig: (schedule: IntegrationSchedule) => void;
  onStartEditOptions: (schedule: IntegrationSchedule) => void;
  onStartDeleteSchedule: (schedule: IntegrationSchedule) => void;
  onWorkspaceClick: ((workspace: IntegrationWorkspace) => void) | null;
  noSchedulesMessage: string;
  allowedConfigurations?: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
};

function SchedulesComponent({
  isLoading,
  schedules,
  getConnectionName,
  onRenameSchedule,
  onStartScheduleRenaming,
  onPauseSchedule,
  onRunSchedule,
  onEditSchedule,
  onStartCreateConfig,
  onStartEditOptions,
  onStartDeleteSchedule,
  onWorkspaceClick,
  noSchedulesMessage,
  allowedConfigurations = true,
  i18nLocale,
  locale,
}: SchedulesComponentProps) {
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const { editingId, editingName, setEditingId, setEditingName, clear } =
    useEditableConfig();

  const { sortOrder, sortBy, getSortableColumnProps } = useSortableTable({
    initialSortBy: 'jobOptions.name',
    initialSortOrder: SortOrder.ASC,
  });

  const sortedSchedules = smartSort(schedules, sortBy, sortOrder);

  return (
    <DatasourceTable
      idIndex="_id"
      loading={isLoading}
      dataSource={sortedSchedules}
      components={newTableTheme}
      expandedRowIds={expandedRowIds}
      getExpandedRow={schedule => (
        <ExpandedSchedule
          schedule={schedule}
          connectionName={getConnectionName(schedule)}
          onWorkspaceClick={onWorkspaceClick}
          i18nLocale={i18nLocale}
          locale={locale}
        />
      )}
      columns={[
        {
          dataIndex: 'jobOptions.name',
          title: t(i18nLocale, 'integrations:schedules.columns.name', 'Name'),
          valueRender: (_, schedule) =>
            schedule._id === editingId ? (
              <EditableCell
                initValue={schedule.jobOptions.name}
                onValueChange={setEditingName}
                onSubmit={() => {
                  if (!editingName) return;
                  onRenameSchedule(editingName, schedule);
                }}
                onClear={clear}
              />
            ) : (
              <div>{schedule.jobOptions.name}</div>
            ),
          ...getSortableColumnProps('jobOptions.name'),
        },
        {
          title: t(i18nLocale, 'integrations:schedules.columns.type', 'Type'),
          valueRender: (_, { jobOptions }) => (
            <Capitalized>
              {getScheduleDirectionId(jobOptions.type) === 'import'
                ? t(
                    i18nLocale,
                    'integrations:schedules.directionImport',
                    'import'
                  )
                : t(
                    i18nLocale,
                    'integrations:schedules.directionExport',
                    'export'
                  )}
            </Capitalized>
          ),
          ...getSortableColumnProps('jobOptions.type'),
        },
        {
          title: t(
            i18nLocale,
            'integrations:schedules.columns.interval',
            'Interval'
          ),
          valueRender: (_, { schedulingPolicy }) => (
            <SchedulePolicy
              schedulingPolicy={schedulingPolicy}
              i18nLocale={i18nLocale}
            ></SchedulePolicy>
          ),
          ...getSortableColumnProps('schedulingPolicy.type'),
        },
        {
          dataIndex: 'meta.lastCompleted',
          title: t(
            i18nLocale,
            'integrations:schedules.columns.lastSync',
            'Last sync'
          ),
          valueRender: lastCompleted => (
            <IntegrationDate
              date={lastCompleted}
              format="DATENEW"
              showIcon
              locale={locale}
              i18nLocale={i18nLocale}
            />
          ),
          ...getSortableColumnProps('meta.lastCompleted'),
        },
        {
          title: t(
            i18nLocale,
            'integrations:schedules.columns.status',
            'Status'
          ),
          valueRender: (_, { meta }) => (
            <ScheduleStatus
              meta={meta}
              i18nLocale={i18nLocale}
            ></ScheduleStatus>
          ),
          ...getSortableColumnProps('meta.lastResult.status'),
        },
        {
          dataIndex: '_id',
          valueRender: id => (
            <ExpandRowButton
              expandedRowIds={expandedRowIds}
              setExpandedRowIds={setExpandedRowIds}
              rowId={id}
              i18nLocale={i18nLocale}
            />
          ),
        },
        {
          valueRender: (_, schedule) => (
            <CellWithDropdown
              options={[
                ...(schedule.meta.paused
                  ? [
                      {
                        label: t(
                          i18nLocale,
                          'integrations:schedules.actions.resume',
                          'Resume'
                        ),
                        onClick: () => onEditSchedule(schedule._id),
                      },
                    ]
                  : [
                      {
                        label: t(
                          i18nLocale,
                          'integrations:schedules.actions.editSchedule',
                          'Edit schedule'
                        ),
                        onClick: () => onStartEditOptions(schedule),
                      },
                      {
                        label:
                          getScheduleDirectionId(schedule.jobOptions.type) ===
                          'import'
                            ? t(
                                i18nLocale,
                                'integrations:schedules.actions.editImport',
                                'Edit import'
                              )
                            : t(
                                i18nLocale,
                                'integrations:schedules.actions.editExport',
                                'Edit export'
                              ),
                        onClick: () => onEditSchedule(schedule._id),
                      },
                      {
                        label:
                          getScheduleDirectionId(schedule.jobOptions.type) ===
                          'import'
                            ? t(
                                i18nLocale,
                                'integrations:schedules.actions.importNow',
                                'Import now'
                              )
                            : t(
                                i18nLocale,
                                'integrations:schedules.actions.exportNow',
                                'Export now'
                              ),
                        onClick: () => onRunSchedule(schedule._id),
                      },
                    ]),
                ...(allowedConfigurations
                  ? [
                      {
                        label: t(
                          i18nLocale,
                          'integrations:schedules.actions.createConfiguration',
                          'Create configuration'
                        ),
                        onClick: () => {
                          onStartCreateConfig(schedule);
                        },
                      },
                    ]
                  : []),
                ...(schedule.meta.paused
                  ? []
                  : [
                      {
                        label: t(
                          i18nLocale,
                          'integrations:schedules.actions.pause',
                          'Pause'
                        ),
                        onClick: () => onPauseSchedule(schedule._id),
                      },
                      {
                        label: t(
                          i18nLocale,
                          'integrations:schedules.actions.rename',
                          'Rename'
                        ),
                        onClick: () => {
                          onStartScheduleRenaming();
                          setEditingId(schedule._id);
                        },
                      },
                    ]),
                {
                  label: t(
                    i18nLocale,
                    'integrations:schedules.actions.delete',
                    'Delete'
                  ),
                  onClick: () => onStartDeleteSchedule(schedule),
                },
              ]}
            />
          ),
        },
      ]}
      renderEmptyTable={() =>
        !isLoading ? (
          <DataSourceEmptyTable
            title={t(
              i18nLocale,
              'integrations:schedules.emptyTitle',
              "You don't have any schedules yet"
            )}
            message={noSchedulesMessage}
          />
        ) : null
      }
      scrollableSectionHeight="100%"
      fixedHeader
    />
  );
}

const Capitalized = styled.div`
  text-transform: capitalize;
`;

export const Schedules = connect(SchedulesComponent, viewModel$);
